/**
 * Import Dependency
 */
import ChargerLocation from '@/models/ChargerLocation'

/**
 * Import API
 */
import Vue from 'vue'

// Avoid excessive use of Vue
// This is static read only data
const chargingPointsByUUID = {}

/**
 * Declare Variable
 */
const state = {
  loading: false,

  //
  chargingPointsByCode: {},
  refreshByCode: {},
}

const getters = {
  /**
   * Chargingpoint by UUID
   */
  getChargingPointByUUID: () => ({ uuid }) => {
    if (! uuid) return null

    return chargingPointsByUUID[uuid] || null
  },

  getChargingPointsByCode: (state) => ({ code }) => {
    if (! code) return []
    return state.chargingPointsByCode[code] || []
  },

  /**
   * If not set, it means we have not loaded the full collection of that geography code
   *  The refresh TS can be used to only get all changes from the listed TS
   */
  getRefreshByCode: (state) => ({ code }) => {
    return state.refreshByCode[code] || null
  },
}
const actions = {
  loadLocationRecordsFromCBAPI: async function(
    { state, commit },
    { codes, progressCallback },
  ) {

    if (state.loading) return
    commit('setLoadingState', { loading: true })

    let records = await this.$cbapi.utility.paginatedAPICall({
      apiCall: this.$cbapi.locations.getCollectionPage,
      params: {
        codes,
      },
      progressCallback,
    })

    commit('setRecords', {
      records: records.map(
        location => Object.freeze(new ChargerLocation({ data: location, cbapi: true })),
      ),
    })
    commit('setLoadingState', { loading: false })
  },

  /**
   * Load records of a specific code
   */
  loadLocationRecordsByCode: async function(
    { state, getters, commit },
    { code, progressCallback, refresh = null },
  ) {
    if (state.loading) return
    commit('setLoadingState', { loading: true })

    if (refresh === true) {
      refresh = getters.getRefreshByCode({ code })
    }

    let records = await this.$cbapi.utility.paginatedAPICall({
      apiCall: this.$cbapi.locations.getCollectionPage,
      params: {
        codes: [code],
        fromTimeStamp: refresh,
      },
      progressCallback,
    })

    commit('setRecordsByCode', {
      records: records.map(
        location => Object.freeze(new ChargerLocation({ data: location, cbapi: true })),
      ),
      code,
    })
    commit('setLoadingState', { loading: false })
  },
}
const mutations = {
  setLoadingState(state, { loading }) {
    state.loading = loading
  },

  /**
   * Accepts a list of records from multiple geographies
   *  Set norefresh to true if only loading a subset of the collection related to a code (e.g. only locations related to RPs)
   */
  setRecords(state, { records, norefresh = false }) {

    // Local cache - to avoid calling Vue.set for each record
    let recordsByCode = {}
    let refreshByCode = {}

   // console.log('setRecords', records?.[0], records?.[0]?.getUuid())

    records.forEach(record => {
      const code = record.getGeographyCode()
      const uuid = record.getUuid()

      // Avoid issues
      if (! code) {
        console.log('MISSING GEOGRAPHY CODE...', uuid)
        return
      }

      // For the first record within a code, start a cached list
      // Use the currently loaded records as starting point
      // Don't throw away old locations, because it's possible to only
      // load records that have changed since a timestamp
      // TODO: CBAPI - how to deal with deleted records ? Is that even relevant ?
      if (! recordsByCode[code]) {
        recordsByCode[code] = state.chargingPointsByCode[code] || []

        // Get TS of most recently changed record
        refreshByCode[code] = Math.max(
          state.refreshByCode[code] || 0,
          refreshByCode[code] || 0,
          record.getSyncedTS(),
        )
      }

      // Was this record previously loaded ?
      // If so, we need to search & replace.
      // Otherwise we can just add it to the list
      if (chargingPointsByUUID[uuid]) {
        let recordIndex = recordsByCode[code]
          .findIndex(current => current.uuid === uuid)

        recordsByCode[code].splice(recordIndex, 1, record)

      } else {
        recordsByCode[code].push(record)
      }

      // Either way, we can just set this one
      chargingPointsByUUID[uuid] = record
    })

    // Go through the codes that had location changes
    Object.keys(recordsByCode).forEach(code => {
      Vue.set(state.chargingPointsByCode, code, recordsByCode[code])

      if (norefresh !== true) {
        Vue.set(state.refreshByCode, code, refreshByCode[code])
      }
    })
  },

  /**
   * Set records for a specific geography code
   */
  setRecordsByCode(state, { records, code }) {
    let recordsByCode = state.chargingPointsByCode[code] || []

    let refreshByCode = state.refreshByCode[code] || 0

    records.forEach(record => {
      const uuid = record.getUuid()

      // Was this record previously loaded ?
      // If so, we need to search & replace.
      // Otherwise we can just add it to the list
      if (chargingPointsByUUID[uuid]) {
        let recordIndex = recordsByCode
          .findIndex(current => current.uuid === uuid)

        recordsByCode.splice(recordIndex, 1, record)
      } else {
        recordsByCode.push(record)
      }

      // Get TS of most recently changed record
      refreshByCode = Math.max(refreshByCode, record.getSyncedTS())

      // Either way, we can just set this one
      chargingPointsByUUID[uuid] = record
    })

    Vue.set(state.chargingPointsByCode, code, recordsByCode)
    Vue.set(state.refreshByCode, code, Date.now())
  },

  /**
   * Replace a specific Location record
   */
  replaceLocation(state, { location }) {

    location = Object.freeze(
      new ChargerLocation({
        data: location,
        cbapi: true,
      }),
    )

    let code = location.getGeographyCode()
    let uuid = location.getUuid()

    // Avoid issues
    if (! code) {
      console.log('MISSING GEOGRAPHY CODE...', uuid)
      return
    }

    let recordsByCode = state.chargingPointsByCode[code] || []

    // Replace if already loaded
    // Otherwise just add
    if (recordsByCode.length !== 0 && chargingPointsByUUID[uuid]) {
      let recordIndex = recordsByCode
        .findIndex(current => current.uuid === uuid)

      recordsByCode.splice(recordIndex, 1, location)
    } else {
      recordsByCode.push(location)
    }

    // Get TS of most recently changed record. Probably this one
    let refreshByCode = Math.max(
      state.refreshByCode[code] || 0,
      location.getSyncedTS(),
    )

    // Either way, we can just set this one
    chargingPointsByUUID[uuid] = location

    Vue.set(state.chargingPointsByCode, code, recordsByCode)

    /**
     * If there is no refresh TS yet, it means we only have a subset of the locations of the
     *  geography code. Setting the TS would create the impression that we actually have them all.
     */
    if (state.refreshByCode[code]) {
      Vue.set(state.refreshByCode, code, refreshByCode)
    }
  },
}

/**
 * Export
 */
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
